<template>
  <b-card>
    <b-card-body>
      <div class="overflow-x-auto">
        <div class="filter_Documentacion">
          <PSidebar
            v-model="mostrarFiltros"
            :title="$t('label.filtros')"
            icon="IconFilter"
            bVariant="secondary"
            :textButton="dameTextoBotonFiltros"
          >
            <documentacion-non-historical-table-filters
              v-model="filters"
              :tiposDocumentos="tiposDocumentos"
              actionName="filtrar"
              :textActionName="$t('label.filtrar')"
              :maxRangeMonth="maxRangeMonthDate"
              @action="action"
              @cancel="action"
            />
          </PSidebar>
        </div>
        <QuickFilter
          :params.sync="params"
          @change="onChangeQuickFilter"
          :showOptionTodos="true"
          :documentType="'ByNonHistorical'"
        />
        <div class="table-responsive">
          <hr />

          <b-table
            ref="tableDocumentos"
            :fields="visibleFields"
            :items="items"
            :sort-by.sync="params.orderBy"
            :sort-desc.sync="sortDesc"
            :empty-text="$t('label.noData')"
            no-border-collapse
            show-empty
            sort-icon-left
            @sort-changed="onSortingChange"
            responsive
            no-local-sorting
            borderless
          >
            <div class="text-center loader my-2" slot="table-busy">
              <b-spinner class="align-middle" />
            </div>
            <template #head(add-columns)>
              <select-columns :fields.sync="fields" :pagination="params" :cod-table="codTable" />
            </template>
            <template #cell(active)="row">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="row.item.id"
                  v-model="row.item.active"
                />
                <label class="custom-control-label" :for="row.item.id" />
              </div>
            </template>
            <template #cell(documentType)="row">
              {{
                $te(`documento.tipo.${row.item.documentType?.toLowerCase()}`)
                  ? $t(`documento.tipo.${row.item.documentType?.toLowerCase()}`)
                  : row.item.documentType
              }}
            </template>
            <template #cell(actions-left)="row">
              <div class="table-button-container">
                <button
                  :id="`downloadFile-${row.index}`"
                  class="mx-1 my-auto"
                  @click="download(row.item)"
                >
                  <icon-download :height="16" :width="16" color="#000" />
                </button>
                <button
                  v-if="showDelete"
                  :id="`removeFile-${row.index}`"
                  class="ml-1 my-auto"
                  @click="remove(row.item)"
                >
                  <icon-trash :size="16" color="#000" />
                </button>
              </div>
            </template>
          </b-table>
        </div>
        <div
          class="mt-3"
          v-if="documentacionResponse && documentacionResponse.totalElements > params.size"
        >
          <pagination
            :current-page="params.page"
            :page-size="params.size"
            :total-elements="documentacionResponse.totalElements"
            @change="onChangeCurrentPage"
          />
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script lang="ts">
import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import {
  ICliente,
  IDelegacion,
  IDocumentoNonHistoricalTableItem,
  IField,
  IParametro,
  ISoConfColumnasUpdateDTO,
  IUsuario,
} from "@/entities";
import { Action, Getter } from "vuex-class";
import Pagination from "@/components/common/Pagination.vue";
import VueFilters from "@/vue-filters";

import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import QuickFilter from "@/components/documentacion/common/QuickFilter.vue";
import {
  EDirection,
  IColumnaResponse,
  IDocumentacionNonHistoricalFilters,
  IDocumentacionNonHistoricalParams,
  IDocumentacionNonHistoricalResponse,
} from "@/services";
import DocumentacionNonHistoricalTableFilters from "@/components/documentacion/DocumentacionNonHistoricalTableFilters.vue";
import VueMethods from "@/vue-methods";
import { SweetAlertResult } from "sweetalert2";
import { Dictionary } from "vue-router/types/router";
import { IconDownload, IconTrash } from "@/components/look";
import { MAX_RANGE_MONTH_DATE, ROLE_PAIS } from "@/constans";

@Component({
  components: {
    DocumentacionNonHistoricalTableFilters,
    IconDownload,
    IconTrash,
    SelectColumns,
    QuickFilter,
    Pagination,
    MAX_RANGE_MONTH_DATE,
  },
  mixins: [VueFilters, VueMethods],
})
export default class DocumentosNonHistoricalTable extends Vue {
  @Getter("getClienteSelected") getClienteSelected!: ICliente;
  @Action("saveColumna") saveColumna!: (
    params: ISoConfColumnasUpdateDTO
  ) => Promise<IColumnaResponse | undefined>;

  @Getter("getDelegacionesCliente") delegations!: IDelegacion[];

  @Getter("getLoggedUser") getLoggerUser!: IUsuario;
  usuarioLogado: Partial<IUsuario> = {};

  @Prop({ required: false, default: [] })
  fieldsI!: IField[];

  @Prop({ required: true })
  params!: IDocumentacionNonHistoricalParams;

  public maxRangeMonthDate = MAX_RANGE_MONTH_DATE;

  public filters: IDocumentacionNonHistoricalFilters = {
    documentTitle: "",
    documentType: "",
    delegaciones: [],
    fechaDocumentoInicio: "",
    fechaDocumentoFin: "",
  };

  public mostrarFiltros = false;
  public documentacionResponse?: IDocumentacionNonHistoricalResponse | null = null;
  private documentacionResponseList: IDocumentoNonHistoricalTableItem[] = [];
  public codTable = "documentosNonHistorical";
  public sortDesc = true;

  beforeMount(): void {
    this.filters.documentTitle = this.params.documentTitle ?? "";
    this.filters.documentType = this.params.documentType ?? "";
    this.filters.delegaciones = this.params.delegaciones || [];
    this.filters.fechaDocumentoInicio = this.params.fechaDocumentoInicio ?? "";
    this.filters.fechaDocumentoFin = this.params.fechaDocumentoFin ?? "";

    this.sortDesc = this.params.direction === EDirection.DESC;
  }

  mounted(): void {
    this.usuarioLogado = this.getLoggerUser;
  }

  async loadInfo(): Promise<void> {
    const delegacionsSelected = this.delegations?.filter((d) =>
      this.params.delegaciones?.find((code) => code === d.codDelegacionSol)
    );

    this.params.delegaciones = delegacionsSelected.map((d) => d.codDelegacionSol);
    this.filters.delegaciones = delegacionsSelected.map((d) => d.codDelegacionSol);

    await this.updateTiposDocumentos();
    await this.getDocumentacion();
  }

  async onChangeQuickFilter(): Promise<void> {
    this.params.page = 1;
    await this.loadInfo();
  }

  get items(): IDocumentoNonHistoricalTableItem[] {
    return this.documentacionResponseList ?? [];
  }

  public get visibleFields(): IField[] {
    return this.fieldsI.filter((item) => item.visible);
  }

  get fields(): IField[] {
    return JSON.parse(JSON.stringify(this.fieldsI));
  }

  set fields(fields: IField[]) {
    this.$emit("update:fieldsI", fields);
  }

  get dameTextoBotonFiltros(): string {
    let numFiltros = 0;
    Object.keys(this.filters).forEach((key: string) => {
      if ((this.filters as any)[key] != "" && (this.filters as any)[key] != null) {
        numFiltros = numFiltros + 1;
      }
    });

    return numFiltros
      ? `(${numFiltros}) ${this.$t("label.filtros")}`
      : this.$t("label.filtros").toString();
  }

  tiposDocumentos: IParametro[] = [];

  async updateTiposDocumentos(): Promise<void> {
    this.tiposDocumentos = [];
    if (this.getClienteSelected) {
      this.tiposDocumentos =
        (await this.$services.documentacion.fetchTiposDocumentosByPais(
          this.getClienteSelected?.pais?.id
        )) ?? [];
      this.params.documentType = this.tiposDocumentos?.find(
        (tipoDoc) => tipoDoc.codParametro == this.params.documentType
      )?.codParametro;

      this.filters.documentType = this.params.documentType ?? "";
    }
  }

  onChangeCurrentPage(currentPage: number): void {
    this.params.page = currentPage;
    this.getDocumentacion();
  }

  onSortingChange(sort: { sortBy: string; sortDesc: boolean }): void {
    this.$nextTick(async () => {
      this.params.direction = sort.sortDesc ? EDirection.DESC : EDirection.ASC;
      await this.getDocumentacion();
      await this.saveColumnsConfig();
    });
  }

  async saveColumnsConfig(): Promise<void> {
    const params: ISoConfColumnasUpdateDTO = {
      codColumna: this.visibleFields
        .filter((f) => !f.aux)
        .map((f) => f.code)
        .toString(),
      codTabla: this.codTable,
      codTipoOrden: this.params.direction,
      numPagElements: this.params.size,
      codOrden: this.params.orderBy,
    };

    await this.saveColumna(params);
  }

  async getDocumentacion(): Promise<void> {
    this.params.pkCliente = this.getClienteSelected?.id?.toString();

    this.$nextTick(() => {
      this.$router
        .replace({ path: this.$route.path, query: { ...this.params } as Dictionary<any> })
        .catch(() => {
          // Do nothing
        });
    });

    const params = JSON.parse(JSON.stringify(this.params)) as IDocumentacionNonHistoricalParams;
    params.page = params.page ? params.page - 1 : 0;

    this.documentacionResponse = await this.$services.documentacion.fetchDocumentos(params);
    this.documentacionResponseList = [];
    this.documentacionResponse?.content.forEach((documentacion) => {
      this.documentacionResponseList.push({
        id: documentacion.id,
        documentTitle: documentacion.documentTitle,
        documentType: documentacion.documentType,
        pais: documentacion.pais.descripcion,
        cliente: documentacion.cliente.desCliente,
        delegacion: documentacion.delegacion.desDelegacion,
        fechaDocumento: documentacion.fechaDocumento,
      });
    });
  }

  async download(doc: IDocumentoNonHistoricalTableItem): Promise<void> {
    const response = await this.$services.documentacion.downloadFileNonHistorical(doc);

    if (response) {
      this.downloadFile(response);
    } else {
      this.makeToast("danger", this.$t("label.documentation.download.error").toString());
    }
  }

  async remove(doc: IDocumentoNonHistoricalTableItem): Promise<void> {
    const { isConfirmed } = await this.modalConfirmDeleteDocument();
    if (isConfirmed) {
      await this.$services.documentacion.removeNonHistorical(doc);
      await this.getDocumentacion();
      this.makeToast("success", this.$t("document.removed").toString());
    }
  }

  action(action: { action: string; data?: any }): void {
    if (action.action === "download") {
      alert("download");
    } else if (action.action === "filtrar" || action.action === "clean") {
      this.params.page = 1;

      this.params.documentTitle = this.filters.documentTitle ?? undefined;
      this.params.documentType = this.filters.documentType ?? undefined;
      this.params.delegaciones = this.filters.delegaciones;
      this.params.fechaDocumentoInicio = this.getStartDate(this.filters.fechaDocumentoInicio);
      this.params.fechaDocumentoFin = this.getEndDate(this.filters.fechaDocumentoFin);

      this.mostrarFiltros = false;

      this.getDocumentacion();
    }
  }

  async modalConfirmDeleteDocument(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("label.documentation.deletion").toString(),
      text: this.$t("label.documentation.deletion.confirmation.message").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  get showDelete(): boolean {
    return this.usuarioLogado.nivel === ROLE_PAIS;
  }
}
</script>
<style lang="scss" scoped>
.descripcion-cliente {
  margin-left: 10px;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  color: var(--secundario-prosegur-claro);
}

.check-icon {
  color: green;
}

.icon {
  width: 30px;
  color: var(--amarillo-prosegur);
  padding-left: 3px;
}

.filter_Documentacion {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (min-width: 681px) {
    float: right;
  }
}

.material::v-deep .vs__dropdown-toggle {
  border: none;
  cursor: pointer;
}

.material::v-deep .vs__selected-options .vs__selected {
  padding-left: 0;
  margin-top: 0;
  margin-left: 0;
  font-size: 14px;
  color: #808080;
  font-weight: bold;
}

#input-cod-cliente-button {
  width: 150px;
  font-size: 12px;
  padding: 0;
  color: #979797;
  text-align: start;
}

#input-cod-cliente-icon {
  margin-left: 8px;
}

#input-cod-cliente {
  width: 150px;
}

.separator {
  width: 1px;
  height: 100%;
  border: solid 1px #e9e9e9;
}

#divisas-group {
  padding: 0 2em;
}

#input-divisas-label {
  font-size: 12px;
  line-height: 1.33;
  color: #979797;
}

#divisas-group > button {
  margin-right: 10px;
  width: 68px;
  height: 23px;
  object-fit: contain;
  font-size: 12px;
  font-weight: 400;
  text-align: center;
  padding-top: 0.15rem;
  padding-left: 16px;
  padding-right: 16px;
  color: var(--secundario-prosegur-oscuro);
  background-color: var(--fondo-not-active-toggle);
  border-color: var(--fondo-not-active-toggle);
}

#divisas-group > button.active {
  font-weight: 500;
  background-color: var(--amarillo-prosegur);
  border-color: var(--amarillo-prosegur);
}
</style>
